import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { ResaleData } from '../../types/resale';
import { logger } from '../../utils/logger';
import { useApiService } from '../useApiService';
import { useLoaderScreenContext } from './loader-screen.context';

export type ResaleContextProps = {
  resaleData: ResaleData | null;
  loading: boolean;
  hasError: boolean;
  errorType: 'notFound' | 'generic' | null;
  getResaleById: (resaleId: string) => Promise<boolean>;
};

const ResaleContext = createContext({} as ResaleContextProps);

function ResaleProvider({ children }: { children: ReactNode }) {
  const [resaleData, setResaleData] = useState<ResaleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorType, setErrorType] = useState<'notFound' | 'generic' | null>(null);

  const { request } = useApiService();
  const { toggleLoader } = useLoaderScreenContext();

  /**
   * Fetches resale details by ID from the public API endpoint.
   * This function retrieves resale ticket information for purchase including event data.
   *
   * API Response Structure:
   * { responseData: { id, event, ticket, isActive, isSold, ... } }
   *
   * @param resaleId - The unique identifier for the resale transaction
   * @returns Promise<boolean> - True if resale data was fetched successfully, false otherwise
   */
  const getResaleById = useCallback(async (resaleId: string): Promise<boolean> => {
    try {
      toggleLoader({ show : true });
      setLoading(true);
      setHasError(false);
      setErrorType(null);

      const resourcePath = `/resale/${resaleId}?apikey=${Environment.API_KEY}`;

      // useApiService already extracts response.data from axios
      // So response is already the API JSON response
      const response = await request<null, {
        responseData: ResaleData;
        responseDetails: string;
        responseError: string | null;
        responseStatus: number;
      }>(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      // Check for API error response
      if (response?.responseError) {
        logger.error(
          `API error fetching resale ${resaleId}: ${JSON.stringify(response.responseError)}`
        );

        // Check if it's a "Resale not found" error
        const errorObj = response.responseError as unknown;
        const errorCode =
          errorObj &&
          typeof errorObj === 'object' &&
          'code' in errorObj
            ? (errorObj as { code: string }).code
            : null;

        if (errorCode === 'Resale not found') {
          setErrorType('notFound');
        } else {
          setErrorType('generic');
        }

        setHasError(true);
        setResaleData(null);
        return false;
      }

      // Extract the responseData directly from response
      const resaleDataFromApi = response?.responseData;

      if (!resaleDataFromApi) {
        logger.warn(`Resale ${resaleId} not found`);
        setHasError(true);
        setErrorType('notFound');
        setResaleData(null);
        return false;
      }

      // Validate event data is present
      if (!resaleDataFromApi.event) {
        logger.error(`Resale ${resaleId} missing event data`);
        setHasError(true);
        setErrorType('generic');
        setResaleData(null);
        return false;
      }

      // Always set resale data, regardless of availability
      // The page component will decide how to handle unavailable resales
      if (!resaleDataFromApi.isActive || resaleDataFromApi.isSold) {
        logger.warn(`Resale ${resaleId} is not available for purchase`, {
          isActive : resaleDataFromApi.isActive,
          isSold : resaleDataFromApi.isSold,
        });
      }

      setResaleData(resaleDataFromApi);
      return true;
    } catch (error) {
      logger.error(`Failed to fetch resale ${resaleId}: ${error}`);
      setHasError(true);
      setResaleData(null);
      return false;
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
    }
  }, [request, toggleLoader]);

  const shared = {
    resaleData,
    loading,
    hasError,
    errorType,
    getResaleById,
  };

  return (
    <ResaleContext.Provider value={shared}>{children}</ResaleContext.Provider>
  );
}

ResaleProvider.displayName = 'ResaleProvider';

export const useResaleContext = (): ResaleContextProps =>
  useContext(ResaleContext);

export { ResaleContext, ResaleProvider };
